import { type ReactNode, useId, useState } from "react";

import { BarChart3Icon, TableIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { EmptyState } from "@/components/shared";

export interface ChartTableRow {
  key: string;
  label: string;
  values: string[];
}

interface ChartCardProps {
  title: string;
  description?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Grafikning o'zi. */
  children: ReactNode;
  /** Grafikning JADVAL egizagi — har bir qiymat rangdan tashqari yo'l bilan ham o'qiladi. */
  tableHead: string[];
  tableRows: ChartTableRow[];
  emptyTitle: string;
  emptyDescription: string;
  footnote?: ReactNode;
}

/**
 * Grafik kartasi.
 *
 * Har bir grafikning JADVAL EGIZAGI bor va u bir bosishda ochiladi: rang
 * ko'rmaydigan odam ham, skrinrider ham, chop etilgan varaq ham aynan shu
 * raqamlarni oladi. Grafik hech qachon ma'lumotga yagona yo'l emas.
 */
export const ChartCard = ({
  title,
  description,
  isLoading,
  isError,
  onRetry,
  children,
  tableHead,
  tableRows,
  emptyTitle,
  emptyDescription,
  footnote,
}: ChartCardProps) => {
  const { t } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  const bodyId = useId();

  const body = () => {
    if (isLoading) return <Skeleton className="h-56 w-full rounded-lg" />;

    if (isError) {
      return (
        <EmptyState
          title={t("COMMON.LOAD_FAILED")}
          description={t("COMMON.LOAD_FAILED_HINT")}
          className="border-none bg-transparent py-10"
          action={
            onRetry && (
              <Button variant="outline" onClick={onRetry}>
                {t("COMMON.RETRY")}
              </Button>
            )
          }
        />
      );
    }

    if (tableRows.length === 0) {
      return (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          className="border-none bg-transparent py-10"
        />
      );
    }

    if (asTable) {
      return (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {tableHead.map(head => (
                  <TableHead key={head}>{head}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableRows.map(row => (
                <TableRow key={row.key}>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  {row.values.map((value, index) => (
                    <TableCell key={`${row.key}-${index}`} className="tabular-nums">
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }

    return children;
  };

  return (
    <section className="border-border bg-card flex flex-col gap-4 rounded-xl border p-5 shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading text-base font-semibold">{title}</h2>
          {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>

        <Button
          variant="outline"
          size="sm"
          aria-pressed={asTable}
          aria-controls={bodyId}
          onClick={() => setAsTable(previous => !previous)}
        >
          {asTable ? <BarChart3Icon className="size-4" /> : <TableIcon className="size-4" />}
          {asTable ? t("ANALYTICS.VIEW_CHART") : t("ANALYTICS.VIEW_TABLE")}
        </Button>
      </header>

      <div id={bodyId}>{body()}</div>

      {footnote && <p className="text-muted-foreground text-xs">{footnote}</p>}
    </section>
  );
};
